import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import ProjectActionToolbar from '../../components/navigation/ProjectActionToolbar';
import ProjectMetadataCard from './components/ProjectMetadataCard';
import KPIDashboard from './components/KPIDashboard';
import RiskAssessmentPanel from './components/RiskAssessmentPanel';
import DocumentManagementSection from './components/DocumentManagementSection';
import ProjectTimelinePanel from './components/ProjectTimelinePanel';
import StakeholderInfoPanel from './components/StakeholderInfoPanel';
import ComplianceStatusPanel from './components/ComplianceStatusPanel';
import Icon from '../../components/AppIcon';

const ProjectDetailView = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams?.get('id') || 'AFOLU-MX-2024-001';

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchProjectDetails();
  }, [projectId]);

  const fetchProjectDetails = async () => {
    setLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const mockProject = {
        id: projectId,
        name: 'Proyecto de Reforestación y Conservación Forestal Oaxaca',
        country: 'México',
        projectType: 'A/R (Aforestación/Reforestación)',
        standard: 'Verra VCS',
        status: 'Validado',
        totalArea: 4850,
        startDate: '2024-03-15',
        description: `Proyecto AFOLU enfocado en la restauración de ecosistemas forestales degradados en la región de Oaxaca, México. El proyecto implementa prácticas de reforestación con especies nativas, manejo forestal sostenible y conservación de biodiversidad, generando créditos de carbono verificados bajo el estándar Verra VCS.\n\nEl proyecto trabaja en colaboración con comunidades indígenas locales, promoviendo el desarrollo sostenible y la distribución equitativa de beneficios económicos derivados de los créditos de carbono.`,
        totalEmissionsReduced: 45800,
        emissionsPerHectare: 9.44,
        progress: 68,
        creditsGenerated: 32450,
        bufferPool: {
          allocated: 15,
          available: 85,
          status: 'healthy'
        }
      };

      setProject(mockProject);
    } catch (error) {
      console.error('Error fetching project details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    console.log('Edit project:', projectId);
  };

  const handleChat = () => {
    navigate(`/main-dashboard?chat=true&project=${projectId}`);
  };

  const handleGenerateReport = () => {
    navigate(`/report-rendering-view?project=${projectId}`);
  };

  const handleExport = () => {
    console.log('Export project data:', projectId);
  };

  const handleShare = () => {
    console.log('Share project:', projectId);
  };

  const tabs = [
    { id: 'overview', label: 'Resumen General', icon: 'LayoutDashboard' },
    { id: 'timeline', label: 'Cronograma', icon: 'Clock' },
    { id: 'stakeholders', label: 'Participantes', icon: 'Users' },
    { id: 'compliance', label: 'Cumplimiento', icon: 'Scale' }
  ];

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="space-y-6">
              <div className="skeleton h-24 rounded-lg" />
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <div className="skeleton h-64 rounded-lg" />
                  <div className="skeleton h-96 rounded-lg" />
                </div>
                <div className="space-y-6">
                  <div className="skeleton h-64 rounded-lg" />
                  <div className="skeleton h-64 rounded-lg" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!project) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="flex items-center justify-center w-16 h-16 bg-error/10 rounded-full mx-auto mb-4">
              <Icon name="AlertCircle" size={32} color="var(--color-error)" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">Proyecto No Encontrado</h2>
            <p className="text-sm text-muted-foreground caption mb-6">
              No se pudo cargar la información del proyecto solicitado
            </p>
            <button
              onClick={() => navigate('/main-dashboard')}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-all duration-250 focus-ring"
            >
              Volver al Dashboard
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background pt-16">
        <ProjectActionToolbar
          projectId={project?.id}
          projectName={project?.name}
          onEdit={handleEdit}
          onChat={handleChat}
          onGenerateReport={handleGenerateReport}
          onExport={handleExport}
          onShare={handleShare}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          <div className="mb-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm whitespace-nowrap
                    transition-all duration-250 focus-ring flex-shrink-0
                    ${
                      activeTab === tab?.id
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-foreground hover:bg-muted'
                    }
                  `}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <ProjectMetadataCard project={project} />
                <KPIDashboard project={project} />
                <DocumentManagementSection project={project} />
              </div>
              <div className="space-y-6">
                <RiskAssessmentPanel project={project} />
              </div>
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="max-w-4xl mx-auto">
              <ProjectTimelinePanel project={project} />
            </div>
          )}

          {activeTab === 'stakeholders' && (
            <div className="max-w-4xl mx-auto">
              <StakeholderInfoPanel project={project} />
            </div>
          )}

          {activeTab === 'compliance' && (
            <div className="max-w-5xl mx-auto">
              <ComplianceStatusPanel project={project} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProjectDetailView;